import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  logout,
  setIsAuthenticated,
  setOpenAPIReady,
  setDataInitialized,
  setUser,
  bootstrapSession,
} from './store/slices/authSlice';
import { initializeOpenAPI } from './config/openapi';
import LoadingScreen from './components/LoadingScreen';
import Notification from './components/ui/Notification';
import AppRoutes from './routes/AppRoutes';
import OverageApprovalResultModal from './components/license-buy/OverageApprovalResultModal';
import './services/auth';
import './assets/styles/main.css';
import { AppDispatch } from './store/store';
import { useAppSelector } from './hooks/redux';
import { showApprovalResultModal, hideApprovalResultModal } from './store/slices/overageSlice';
import { resetLogoutFlag } from './services/auth';
import { captureReferralParams } from './utils/referralCookies';

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const selectedTheme = useSelector((state: any) => state.theme.selectedTheme);
  const approvalResultModal = useAppSelector((state) => state.overage.approvalResultModal);

  // Capture affiliate `?ref=` / `?coupon=` params into cookies (last click
  // wins) on every app entry, before any routing.
  useEffect(() => {
    captureReferralParams(window.location.search);
  }, []);

  // Check for overage approval result in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const overageApprovalResult = urlParams.get('overage_approval_result');
    
    if (overageApprovalResult && overageApprovalResult === 'true') {
      dispatch(showApprovalResultModal({
        result: 'success',
      }));
    }
  }, [dispatch]);

  const handleCloseModal = () => {
    dispatch(hideApprovalResultModal());
  };

  /**
   * Mount-only bootstrap.
   *
   * Single effect (no companion "reinitialize" effect) so the bootstrap
   * cannot fire twice on a logged-in reload. Auth pages drive the post-login
   * bootstrap themselves through `completeAuthSession`, so this effect only
   * needs to handle the "fresh page reload with token in storage" case.
   */
  useEffect(() => {
    const initializeApp = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);

          initializeOpenAPI(token);
          dispatch(setOpenAPIReady(true));
          dispatch(setUser(userData));
          dispatch(setIsAuthenticated(true));
          resetLogoutFlag();

          const ok = await dispatch(bootstrapSession()).unwrap();
          if (ok) {
            dispatch(setDataInitialized(true));
          }
        } catch (e) {
          console.error('Auth initialization error:', e);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          initializeOpenAPI(undefined);
          dispatch(setOpenAPIReady(false));
          dispatch(setDataInitialized(false));
          dispatch(logout());
        }
      } else {
        initializeOpenAPI(undefined);
        dispatch(setOpenAPIReady(false));
        dispatch(setIsAuthenticated(false));
      }

      setIsInitializing(false);
    };

    initializeApp();
  }, [dispatch]);

  // Handle theme
  useEffect(() => {
    const rootEl = document.documentElement;
    if (selectedTheme === 'dark') {
      rootEl.classList.add('dark-theme');
    } else {
      rootEl.classList.remove('dark-theme');
    }
    return () => {
      rootEl.classList.remove('dark-theme');
    };
  }, [selectedTheme]);
  
  if (isInitializing) {
    return <LoadingScreen />;
  }

  return (
    <div className="app">
      <BrowserRouter>
        <Notification />
        <AppRoutes />
        <OverageApprovalResultModal
          isVisible={approvalResultModal.isVisible}
          onClose={handleCloseModal}
          result={approvalResultModal.result}
        />
      </BrowserRouter>
    </div>
  );
};

export default App;
